import { useEffect, useMemo, useState } from "react";
import {
  fetchHomeBootstrap,
  fetchTodayWater,
  addWaterLog,
} from "../services/homeService";

import {
  getDoseDate,
  isCompletedDose,
  sameLocalDay,
} from "../utils/homeFormatters";

const PROFILE_REDIRECT_KEY = "mariam:profileRedirected:v1";
const WATER_GOAL = 8;

export function useHomeData({ navigate }) {
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [profile, setProfile] = useState(null);

  const [doseSummary, setDoseSummary] = useState({
    pending: 0,
    scheduled: 0,
  });

  const [todayDoseTotal, setTodayDoseTotal] = useState(0);
  const [todayDoseDone, setTodayDoseDone] = useState(0);
  const [nextDoseAt, setNextDoseAt] = useState(null);

  const [waterCups, setWaterCups] = useState(0);
  const [waterSaving, setWaterSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      try {
        const { me, profile: serverProfile, doses, waterToday } =
          await fetchHomeBootstrap();

        setUserName(me?.name || me?.user?.name || "");
        setProfile(serverProfile || null);

        // 🔒 regra de obrigatoriedade do perfil
        try {
          const p = serverProfile || {};
          const gender = String(p.sex || p.gender || "").trim();

          const filled = Boolean(
            gender && p.startWeight && p.goalWeight && p.height
          );

          const alreadyRedirected =
            localStorage.getItem(PROFILE_REDIRECT_KEY) === "1";

          if (!filled && !alreadyRedirected) {
            localStorage.setItem(PROFILE_REDIRECT_KEY, "1");

            navigate("/app/profile", {
              replace: true,
              state: { reason: "profile_required", from: "/app/home" },
            });

            return;
          }
        } catch {
          // segue fluxo
        }

        const list = Array.isArray(doses) ? doses : [];
        const now = new Date();

        let pending = 0;
        let scheduled = 0;
        let totalToday = 0;
        let doneToday = 0;
        let nextFuture = null;

        for (const dose of list) {
          const doseDate = getDoseDate(dose);
          if (!doseDate) continue;

          const completed = isCompletedDose(dose);

          if (!completed) {
            if (doseDate.getTime() > now.getTime()) {
              scheduled++;

              if (!nextFuture || doseDate < nextFuture) {
                nextFuture = doseDate;
              }
            } else {
              pending++;
            }
          }

          if (sameLocalDay(doseDate, now)) {
            totalToday++;
            if (completed) doneToday++;
          }
        }

        setDoseSummary({ pending, scheduled });
        setTodayDoseTotal(totalToday);
        setTodayDoseDone(doneToday);
        setNextDoseAt(nextFuture);

        setWaterCups(Number(waterToday?.cups || 0));
      } catch {
        setUserName("");
        setProfile(null);
        setDoseSummary({ pending: 0, scheduled: 0 });
        setTodayDoseTotal(0);
        setTodayDoseDone(0);
        setNextDoseAt(null);
        setWaterCups(0);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  const handleAddWater = async () => {
    if (waterSaving) return;

    setWaterSaving(true);

    try {
      await addWaterLog({ cups: 1, ml: 250 });

      const updated = await fetchTodayWater();
      setWaterCups(Number(updated?.cups || 0));
    } catch {
      // fallback otimista
      setWaterCups((prev) => Number(prev || 0) + 1);
    } finally {
      setWaterSaving(false);
    }
  };

  const sex = useMemo(() => {
    const value = String(profile?.sex || profile?.gender || "").toLowerCase();

    if (value.includes("femin")) return "female";
    if (value.includes("mascul")) return "male";

    return "";
  }, [profile]);

  const firstName = useMemo(() => {
    const value = (userName || "").trim();
    if (!value) return "";
    return value.split(" ")[0];
  }, [userName]);

  const medPercent = useMemo(() => {
    if (!todayDoseTotal) return 0;
    return todayDoseDone / todayDoseTotal;
  }, [todayDoseDone, todayDoseTotal]);

  const waterPercent = useMemo(() => {
    if (!WATER_GOAL) return 0;
    return Math.min(1, Number(waterCups || 0) / WATER_GOAL);
  }, [waterCups]);

  const doneCare = useMemo(() => {
    const cappedWater = Math.min(Number(waterCups || 0), WATER_GOAL);
    return Number(todayDoseDone || 0) + cappedWater;
  }, [todayDoseDone, waterCups]);

  const totalCare = useMemo(() => {
    return Number(todayDoseTotal || 0) + WATER_GOAL;
  }, [todayDoseTotal]);

  const carePercent = useMemo(() => {
    if (!totalCare) return 0;
    return Math.min(1, doneCare / totalCare);
  }, [doneCare, totalCare]);

  return {
    loading,
    userName,
    firstName,
    profile,
    sex,
    doseSummary,
    todayDoseTotal,
    todayDoseDone,
    nextDoseAt,
    waterCups,
    waterSaving,
    medPercent,
    waterPercent,
    doneCare,
    totalCare,
    carePercent,
    handleAddWater,
  };
}